import { PureComponent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { generateId } from '../../utils/generateId';
import { getLastOpenDialog } from './utils';

type PortalProps = {
  children: ReactNode;
  rootElementRef?: HTMLElement;
};

type PortalState = {
  isMounted: boolean;
};

/**
 * The portal component renders content on the document `body` unless `rootElementRef` is passed.
 */
export class Portal extends PureComponent<PortalProps, PortalState> {
  id = generateId('portal');

  portalNode: HTMLElement | null = null;

  attachTo: HTMLElement | null = null;

  constructor(props: PortalProps) {
    super(props);
    this.state = {
      isMounted: false,
    };
  }

  componentDidMount() {
    const { rootElementRef } = this.props;

    this.portalNode = document.createElement('div');
    this.portalNode.id = `portal-${this.id}`;
    this.portalNode.style.cssText = 'position: absolute; top: 0; left: 0;';

    this.attachTo = rootElementRef || getLastOpenDialog() || document.body;
    this.attachTo.appendChild(this.portalNode);

    this.setState({ isMounted: true });
  }

  componentWillUnmount() {
    if (this.portalNode && this.attachTo) {
      if (document.contains(this.attachTo) && this.attachTo.contains(this.portalNode)) {
        this.attachTo.removeChild(this.portalNode);
      }
    }
  }

  render(): React.ReactPortal | null {
    const { children } = this.props;
    const { isMounted } = this.state;

    return this.portalNode && isMounted ? createPortal(children, this.portalNode) : null;
  }
}
